import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FinancialTemplate from "templates/financial";
import { useSelector, useDispatch } from "react-redux";
import {
  getFinances,
  getFinancialStatus,
  getStoreTransactions,
} from "services/modules/financial";
import { GetScrollVendorsRequest } from "services/modules/vendors";
import setAuthToken from "services/modules/utils/handel_api";

function FinancialPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const viewMode = searchParams.get("view") || "table";

  const dispatch = useDispatch();
  const { finances, financialStatus, storeTransactions, isLoading } =
    useSelector((state) => state.financial);
  const {
    filterVendors,
    listCount: vendorsCount,
    load: vendorsLoad,
  } = useSelector((state) => state.vendors);

  const isAdmin = localStorage.getItem("user_type") === "AdminUser";

  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  // Mapped/computed states can be derived directly from redux props or kept in simpler variables,
  // but keeping statsData structure for template compatibility.
  // We can map redux data to what template needs.

  const [transactionsPage, setTransactionsPage] = useState(1);

  const fetchFinances = (filters) => {
    dispatch(getFinances({ params: { ...filters } }));
  };

  const fetchCollection = () => {
    // Fetch both status and transactions for the collection view
    const storeId = filterObj?.store_id;
    dispatch(getFinancialStatus({ storeId }));
    dispatch(
      getStoreTransactions({
        params: { page: transactionsPage, store_id: storeId },
      }),
    );
  };

  useEffect(() => {
    if (viewMode === "table" && filterObj) {
      fetchFinances(filterObj);
    }
  }, [filterObj, viewMode]);

  useEffect(() => {
    if (viewMode === "stats") {
      fetchCollection();
    }
  }, [viewMode, transactionsPage, filterObj]);

  // Derived data
  const list =
    finances.data?.map((item) => ({
      id: item.order_id,
      productName: item.product_name,
      date: item.date?.split("T")[0],
      orderNumber: item.order_number,
      cadeauPercentage: item.cadeau_percentage + "%",
      amountDue: item.vendor_amount + " SAR",
    })) || [];

  const count = finances.meta?.total_count || 0;

  const statsData = {
    remaining: financialStatus.remaining_money || 0,
    transferred: financialStatus.total_deserved_money || 0,
    total: financialStatus.total_transactions || 0,
  };

  const transactionsList = storeTransactions.data || [];
  const transactionsCount = storeTransactions.meta?.total_count || 0;

  const handleTransactionsPageChange = (p) => {
    setTransactionsPage(p);
  };

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  // mappedData to format cells if needed
  const mappedData = list; // Already mapped in fetch

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: viewMode === "table" ? false : true,
      path: "/financial",
      title: t("financial.title"),
    },
    viewMode === "stats" && {
      active: false,
      path: "/financial",
      title: t("financial.collection"),
    },
  ];

  const getVendors = (filters) => {
    dispatch(
      GetScrollVendorsRequest({
        params: { ...filters, min_details: true, page_size: 10 },
      }),
    );
  };

  const filterList = [
    ...(isAdmin
      ? [
          {
            type: "filter",
            placeholder: t("financial.vendors"),
            onChange: (value) => {
              setFilterObj((s) => {
                return { ...s, store_id: value?.value, page_number: 1 };
              });
            },
            onMenuOpen: () => {
              (!filterVendors || filterVendors?.length === 0) &&
                getVendors({ page_number: 1 });
            },
            options: filterVendors,
            getList: getVendors,
            count: vendorsCount,
            applySearchFromRequest: true,
          },
        ]
      : []),
  ];

  const handleToggleView = (mode) => {
    if (mode === "stats") {
      navigate("?view=stats");
    } else {
      navigate("/financial");
    }
  };

  const isCollectionDisabled = isAdmin && !filterObj?.store_id;

  return (
    <FinancialTemplate
      data={mappedData}
      count={count}
      paginationPage={page}
      handlePageChange={handlePageChange}
      breadcrumbs={breadcrumbs}
      viewMode={viewMode}
      onToggleView={handleToggleView}
      statsData={statsData}
      filterObj={filterObj}
      setFilterObj={setFilterObj}
      loading={isLoading} // Map redux isLoading
      transactionsList={transactionsList}
      transactionsCount={transactionsCount}
      transactionsPage={transactionsPage}
      handleTransactionsPageChange={handleTransactionsPageChange}
      filterList={filterList}
      isCollectionDisabled={isCollectionDisabled}
    />
  );
}

export default FinancialPage;
